import { Box, useMediaQuery, useTheme } from "@mui/material";
import { GridColDef, GridColumnHeaderTitle } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import DisplayDate from "../../components/DisplayDate";
import ExamPacsAction from "../../components/Exams/ExamPacsAction/ExamPacsAction";
import ExamReportAction from "../../components/Exams/ExamReportAction/ExamReportAction";
import ExamsRedirectFile from "../../components/Exams/ExamsRedirectFile/ExamsRedirectFile";
import BaseTable from "../../components/UI/Table/BaseTable";
import useSiteApp from "../../hooks/useSiteApp";
import { IExams } from "../../models/exams";
import { useExtensionConfigs } from "../../hooks/useExtensionConfigs";
import { Extension } from "../../models/intalled-extensions";
import {
  IExamsTableProps,
  IExamsWithPacsValidation,
} from "../../components/Exams/ExamPacsAction/models";
import Loading from "../../components/Loading";

const translationPath = "page.exams.";

const ExamsTable: React.FC<IExamsTableProps> = ({
  data,
  isDoctor,
  tableProps,
  individualId,
  hasPixeonExtension,
  hasVueMotionExtension,
  hasExamPacsAction,
  loadingPacsValues,
  pacsColumnName,
}) => {
  const { t } = useTranslation();

  const {
    config: { sideMenu },
  } = useSiteApp();

  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const [cdnUrl, setCdnUrl] = useState<boolean>(false);
  const [checkPdfSerialOnNullFormat, setCheckPdfSerialOnNullFormat] =
    useState<boolean>(false);

  const [imagesColumnTitle, setImagesColumnTitle] = useState<string | null>(
    null,
  );
  const [showImagesColumn, setShowImagesColumn] = useState<boolean>(true);
  const [loadingConfigs, setLoadingConfigs] = useState<boolean>(true);

  const { fetchExtensionConfig } = useExtensionConfigs();

  const fetchExtensions = useCallback(async () => {
    const configCdnUrl = await fetchExtensionConfig(Extension.EXAMS, "CDNURL");
    if (!configCdnUrl || configCdnUrl.value === ".") {
      setCdnUrl(false);
    } else {
      setCdnUrl(true);
    }

    const configCheckPdfSerialOnNullFormat = await fetchExtensionConfig(
      Extension.EXAMS,
      "CheckPdfSerialOnNullFormat",
    );
    setCheckPdfSerialOnNullFormat(
      configCheckPdfSerialOnNullFormat?.value === "1",
    );

    const configImagesColumnTitle = await fetchExtensionConfig(
      Extension.EXAMS,
      "ImagesColumnTitle",
    );
    setImagesColumnTitle(configImagesColumnTitle?.value?.trim() || null);

    const configShowImagesColumn = await fetchExtensionConfig(
      Extension.EXAMS,
      "ShowImagesColumn",
    );
    // Sem a config, a coluna continua visível
    setShowImagesColumn(
      !configShowImagesColumn ||
        configShowImagesColumn.value?.trim().toLowerCase() !== "false",
    );

    setLoadingConfigs(false);
  }, [fetchExtensionConfig]);

  useEffect(() => {
    fetchExtensions();
  }, [fetchExtensions]);

  const fixMalformedYear = (dateStr: string): string => {
    if (!dateStr) return dateStr;
    const match = dateStr.match(/^(\d{4})(-.+)$/);
    if (!match) return dateStr;
    const year = parseInt(match[1], 10);
    if (year < 100) {
      const correctedYear = 2000 + (year % 100);
      return `${correctedYear}${match[2]}`;
    }
    return dateStr;
  };

  const columns: GridColDef<IExams | IExamsWithPacsValidation>[] =
    useMemo(() => {
      const imagesColumn: GridColDef<IExams | IExamsWithPacsValidation> = {
        field: "sequencialImageControls",
        flex: 1,
        maxWidth: 100,
        renderHeader: (params) => (
          <GridColumnHeaderTitle
            label={imagesColumnTitle || t(translationPath + "image")}
            columnWidth={params.colDef.computedWidth}
          />
        ),
        renderCell: (params) =>
          params.value.length === 0 ? (
            "-"
          ) : (
            <ExamsRedirectFile
              sequencialControl={params.row.sequencialControl}
              sequencialImageControls={params.row.sequencialImageControls}
            />
          ),
      };

      const baseColumns: GridColDef<IExams | IExamsWithPacsValidation>[] = [
        {
          field: "type",
          headerName: `${t(translationPath + "type")}`,
          flex: 1,
        },
        {
          field: "medicalCertificateName",
          headerName: `${t(translationPath + "procedure")}`,
          flex: 1,
        },
        {
          field: "physicianName",
          headerName: `${t(translationPath + "doctor")}`,
          flex: 1,
        },
        {
          field: "establishmentName",
          headerName: `${t(translationPath + "unit")}`,
          flex: 1,
        },
        {
          field: "medicalCertificateDate",
          headerName: `${t(translationPath + "date")}`,
          flex: 1,
          maxWidth: 150,
          renderCell: (params) => (
            <DisplayDate typography={{ variant: "body2" }}>
              {dayjs(fixMalformedYear(params.value))}
            </DisplayDate>
          ),
        },
        ...(showImagesColumn ? [imagesColumn] : []),
        {
          field: "download",
          headerName: `${t(translationPath + "report")}`,
          flex: 1,
          maxWidth: 100,
          renderCell: (params) => (
            <ExamReportAction
              exam={params.row}
              cdnUrl={cdnUrl}
              checkPdfSerialOnNullFormat={checkPdfSerialOnNullFormat}
              individualId={individualId}
            />
          ),
        },
      ];

      if (hasExamPacsAction) {
        baseColumns.push({
          field: "pixeonPacs",
          flex: 1,
          maxWidth: 100,
          renderHeader: (params) => (
            <GridColumnHeaderTitle
              label={pacsColumnName || t(translationPath + "pacs")}
              columnWidth={params.colDef.computedWidth}
            />
          ),
          renderCell: ({ row }) => (
            <ExamPacsAction
              exam={row}
              hasPixeonExtension={hasPixeonExtension}
              hasVueMotionExtension={hasVueMotionExtension}
            />
          ),
        });
      }

      return baseColumns;
    }, [
      t,
      individualId,
      cdnUrl,
      checkPdfSerialOnNullFormat,
      hasPixeonExtension,
      hasVueMotionExtension,
      hasExamPacsAction,
      pacsColumnName,
      imagesColumnTitle,
      showImagesColumn,
    ]);

  if (loadingPacsValues || loadingConfigs) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loading />
      </Box>
    );
  }

  return (
    <BaseTable<IExams>
      {...tableProps}
      columns={columns}
      rows={data}
      paginationMode="server"
      sx={{
        height: matches
          ? matches && isDoctor
            ? "calc(100vh - 340px)"
            : "calc(100vh - 320px)"
          : sideMenu
            ? isDoctor && sideMenu
              ? "calc(100vh - 240px)"
              : "calc(100vh - 220px)"
            : "calc(100vh - 240px)",
      }}
    />
  );
};

export default memo(ExamsTable);
